import React, {useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import { Col, Dropdown, Row, } from "react-bootstrap";
import IFormWithConfig from "../../../models/misconfig/FormWithConfig";
import IBromcomProviderConfig from "../../../models/misconfig/bromcom/BromcomProviderConfig";
import BiometricFilterEdit from "../biometric-filter-edit";
import IBiometricsFilter from "../../../models/misconfig/BiometricsFilter";
import IMedicalConditionsFilter from "../../../models/misconfig/bromcom/MedicalConditionsFilter";
import MedicalConditionsFilterEdit from "./medical-filter-edit";

const BromcomProviderConfigEdit = forwardRef((props: IFormWithConfig, ref) => {        
    const [BromcomConfig, setBromcomConfig] = useState<IBromcomProviderConfig | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const numberRegex = /^[1-9]\d*$/;

    //TODO - will be a dropdown of allowed versions, to be supplied by Ben once this implementation is finalised
    const applicationVersions = useMemo(() => [
        { id: 1, value: 6 }
    ], []);

    useEffect(() => {
        if (props.configJson) {
            const configObj: IBromcomProviderConfig = JSON.parse(props.configJson);
            setBromcomConfig(configObj);
        } else {
            setBromcomConfig({
                SchoolID: 0,
                ApplicationVersion: applicationVersions[0].value,
                BiometricConsents: {
                    FaceRecognition: [],
                    Fingerprints: []
                },
                MedicalConditionsFilter: {
                    DietaryNeeds: [],
                    DietaryPreferences: []
                }
            });
        }
    }, [props.configJson, applicationVersions]);

    useImperativeHandle(ref, () => ({
        validate: function() : boolean {
            const newErrors: { [key: string]: string } = {};

            if(!BromcomConfig?.SchoolID || BromcomConfig.SchoolID <= 0) {
                newErrors['SchoolID'] = 'Valid School ID is required';
            }
            if(!BromcomConfig?.ApplicationVersion) {
                newErrors['ApplicationVersion'] = 'Application Version is required';
            }

            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        }
    }));    

    const handleChange = (key: string, value: any) => {
        if(BromcomConfig === null) {
            return;
        }

        if(key === 'SchoolID') {
            if(value === '' || !numberRegex.test(value)) {
                const updatedErrors = { ...errors };
                updatedErrors['SchoolID'] = 'School ID must be a positive number';
                setErrors(updatedErrors);
                return;
            }
            value = parseInt(value);
        }

        const updatedConfig = { ...BromcomConfig, [key]: value };
        setBromcomConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    }

    const handleBiometricFilterChange = (filter: IBiometricsFilter) => {
        if(BromcomConfig === null) {
            return;
        }   
        const updatedConfig = { ...BromcomConfig, BiometricConsents: filter };
        setBromcomConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    };

    const handleMedicalConditionsFilterChange = (filter: IMedicalConditionsFilter) => {
        if(BromcomConfig === null) {
            return;
        }   
        const updatedConfig = { ...BromcomConfig, MedicalConditionsFilter: filter };
        setBromcomConfig(updatedConfig);
        props.setJsonString(JSON.stringify(updatedConfig));
    };

    return (
        <React.Fragment>
             <Row className="pb-3">
                <Col md={props.firstCol} className="pt-2"><b>School ID</b></Col>
                <Col md={props.secondCol}>
                    <input className={`form-control mt-1 ms-0 w-100 ${(errors.SchoolID) ? 'is-invalid' : ''}`} type="text" value={BromcomConfig?.SchoolID} required
                        onChange={e => handleChange('SchoolID', e.target.value)} />  
                    {errors['SchoolID'] && <div className="invalid-feedback d-block">{errors['SchoolID']}</div>}                      
                </Col>
            </Row>
            <Row className="pb-3">
                <Col md={props.firstCol} className="pt-2"><b>Application version</b></Col>
                <Col md={props.secondCol}>
                    <Dropdown className="m-auto" onSelect={(eventKey) => handleChange('ApplicationVersion', eventKey !== null ? Number(eventKey) : null)}>
                        <Dropdown.Toggle className="btn btn-light w-100 d-flex justify-content-between align-items-center border">
                            {applicationVersions.find(v => v.value === BromcomConfig?.ApplicationVersion)?.value || 'Select'}                                                        
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="timer-dropdown w-100">
                            {applicationVersions.map(version => (
                                <Dropdown.Item key={version.id} eventKey={version.value}>
                                    {version.value}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                     {errors['ApplicationVersion'] && <div className="invalid-feedback d-block">{errors['ApplicationVersion']}</div>}
                </Col>
            </Row>
            <BiometricFilterEdit biometricsFilter={BromcomConfig?.BiometricConsents} setBiometricsFilter={handleBiometricFilterChange} />
            <MedicalConditionsFilterEdit medicalConditionsFilter={BromcomConfig?.MedicalConditionsFilter} setMedicalConditionsFilter={handleMedicalConditionsFilterChange} />
        </React.Fragment>
    );
});

export default BromcomProviderConfigEdit;